/**
 * Credit Threshold Logic — Pure function extracted from useCreditBalance hook
 *
 * Determines which credit alert events should fire and which localStorage
 * flags should be set/cleared, without side effects.
 *
 * EXTRACTED_FOR_TESTING — this logic was previously inline in useCreditBalance.js
 */

// Credit threshold constants — email/push alert thresholds (fired from useCreditBalance)
export const THRESHOLD_LOW_500 = 500;
export const THRESHOLD_LOW_100 = 100;
export const THRESHOLD_DEPLETED = 0;

// UI nudge threshold — show in-app low-balance banner/strip when balance is below this value (and > 0)
// Used by: UsageModal upsell banner, AIChatPanel low-balance strip
// Keep in sync with MONETIZE.md §9
export const LOW_CREDIT_UI_THRESHOLD = 300;

// Local storage keys for tracking alert state
export const KEY_ALERTED_500 = 'reedle_credit_alerted_500';
export const KEY_ALERTED_100 = 'reedle_credit_alerted_100';
export const KEY_ALERTED_DEPLETED = 'reedle_credit_alerted_depleted';

/**
 * Pure function: determine credit threshold events and flag operations.
 *
 * Replicates the exact behavior of the original inline logic in useCreditBalance,
 * including the ordering where top-up flag clears happen BEFORE threshold checks
 * (so a top-up that still lands below a threshold will re-fire the alert).
 *
 * @param {number|null} newBalance - Current credit balance
 * @param {number|null} prevBalance - Previous credit balance (null on first load)
 * @param {{ alerted500: boolean, alerted100: boolean, alertedDepleted: boolean }} alertedFlags
 * @returns {{
 *   events: string[],
 *   flagsToSet: string[],
 *   flagsToClear: string[]
 * }}
 */
export function checkCreditThresholds(newBalance, prevBalance, alertedFlags) {
  const events = [];
  const flagsToSet = [];
  const flagsToClear = [];

  if (newBalance === null) {
    return { events, flagsToSet, flagsToClear };
  }

  // Track effective alert state (mutated by clears before threshold checks)
  const effective = { ...alertedFlags };

  // Step 1: Top-up detection — clear flags when balance crosses back above threshold
  if (prevBalance !== null && newBalance > prevBalance) {
    if (prevBalance < THRESHOLD_LOW_500 && newBalance >= THRESHOLD_LOW_500) {
      flagsToClear.push(KEY_ALERTED_500);
      effective.alerted500 = false;
    }
    if (prevBalance < THRESHOLD_LOW_100 && newBalance >= THRESHOLD_LOW_100) {
      flagsToClear.push(KEY_ALERTED_100);
      effective.alerted100 = false;
    }
    if (prevBalance <= THRESHOLD_DEPLETED && newBalance > THRESHOLD_DEPLETED) {
      flagsToClear.push(KEY_ALERTED_DEPLETED);
      effective.alertedDepleted = false;
    }
  }

  // Step 2: Threshold crossing — fire events when balance drops below threshold
  // Uses effective state (after clears) to match original localStorage.removeItem → localStorage.getItem ordering
  if (newBalance < THRESHOLD_LOW_500 && !effective.alerted500) {
    events.push('credit_low_500');
    flagsToSet.push(KEY_ALERTED_500);
  }

  if (newBalance < THRESHOLD_LOW_100 && !effective.alerted100) {
    events.push('credit_low_100');
    flagsToSet.push(KEY_ALERTED_100);
  }

  if (newBalance <= THRESHOLD_DEPLETED && !effective.alertedDepleted) {
    events.push('credits_depleted');
    flagsToSet.push(KEY_ALERTED_DEPLETED);
  }

  return { events, flagsToSet, flagsToClear };
}
